import { useEffect, useRef, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { BentoThemeProvider } from '@zenput/ui-bento';
import { Workbox } from 'workbox-window';
import { ConfirmDialog } from '../../shared/ConfirmDialog';

interface NewVersionUpdateProps {
    onConfirm: (wb: Workbox) => void;
    _t: (translationKey: string) => string;
}

const UPDATE_EVENT_NAME = 'zenput:updateApp';
type UpdateEvent = CustomEvent<{ wb: Workbox }>;

const listenToUpdate = (listener: (event: UpdateEvent) => void) => {
    document.addEventListener(UPDATE_EVENT_NAME, listener);
};

const removeUpdateListener = (listener: (event: UpdateEvent) => void) => {
    document.removeEventListener(UPDATE_EVENT_NAME, listener);
};

export const showUpdatePrompt = (wb: Workbox) => {
    const event: UpdateEvent = new CustomEvent(UPDATE_EVENT_NAME, { detail: { wb } });

    document.dispatchEvent(event);
};

export const NewVersionUpdate = (props: NewVersionUpdateProps) => {
    const { onConfirm, _t } = props;
    const [updating, setUpdating] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const workboxRef = useRef<Workbox | null>(null);

    useEffect(() => {
        const listener = (event) => {
            workboxRef.current = event.detail.wb;
            setShowPrompt(true);
        };

        listenToUpdate(listener);

        return () => {
            removeUpdateListener(listener);
        };
    }, []);

    if (updating) {
        return (
            <Backdrop sx={{ zIndex: 9999999 }} aria-hidden={false} open>
                <CircularProgress color="primary" />
            </Backdrop>
        );
    }

    const onClose = () => {
        setShowPrompt(false);
    };

    return (
        <BentoThemeProvider>
            <ConfirmDialog
                open={showPrompt}
                title={_t('mobile_pwa_update_prompt_title')}
                description={_t('mobile_pwa_update_prompt_description')}
                confirmLabel={_t('mobile_pwa_update_prompt_confirm')}
                cancelLabel={_t('mobile_pwa_update_prompt_cancel')}
                onConfirm={() => {
                    onClose();
                    setUpdating(true);
                    onConfirm(workboxRef.current);
                }}
                onCancel={onClose}
                onClose={onClose}
            />
        </BentoThemeProvider>
    );
};
